import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { ArrowBack as BackIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import { CodeEditor } from '../components/common/CodeEditor';
import { agentFunctionService } from '../services/agentFunctionService';
import { useNotificationStore } from '../store/notificationStore';
import type { AgentFunctionRequestDetailDto } from '../types/agentFunction';
import { MessageRole } from '../types/session';
import { ROUTES } from '../utils/routes';

const getStatusChip = (status: number, size: 'small' | 'medium' = 'small') => {
  const map: Record<number, { label: string; color: 'default' | 'warning' | 'success' | 'error' }> = {
    0: { label: 'Pending', color: 'default' },
    1: { label: 'Processing', color: 'warning' },
    2: { label: 'Completed', color: 'success' },
    3: { label: 'Failed', color: 'error' },
  };
  const cfg = map[status] ?? { label: 'Unknown', color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size={size} />;
};

const getRoleLabel = (role: number) => {
  const map: Record<number, string> = {
    [MessageRole.System]: 'System',
    [MessageRole.User]: 'User',
    [MessageRole.Assistant]: 'Assistant',
    [MessageRole.Tool]: 'Tool',
  };
  return map[role] ?? 'Unknown';
};

const formatOutputJson = (json: string | undefined): string => {
  if (!json || !json.trim()) return '{}';
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
};

export const AgentFunctionRequestDetailPage = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [data, setData] = useState<AgentFunctionRequestDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabIndex, setTabIndex] = useState(0);

  const loadData = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await agentFunctionService.getAgentFunctionRequest(requestId);
      setData(result);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to load request details';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [requestId, addNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 6 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          }
        >
          {error || 'Request not found'}
        </Alert>
      </Box>
    );
  }

  const session = data.session;
  const messages = data.messages ?? [];
  const files = data.files ?? [];

  return (
    <Box sx={{ p: 6, animation: 'fade-in 300ms ease-out' }}>
      {/* Header with back */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton
          size="small"
          onClick={() =>
            navigate(
              data.agentFunctionId
                ? `/agent-functions/${data.agentFunctionId}/requests`
                : '/agent-functions'
            )
          }
          aria-label="Back"
        >
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>
          Execution Request
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Request & Response" />
          <Tab label="Session Data" />
          <Tab label="Files" />
        </Tabs>
      </Box>

      {/* Tab 0: Request & Response */}
      {tabIndex === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Header: Status, Duration, Started At, Completed At */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
            {getStatusChip(data.status, 'medium')}
            <Typography variant="body2" color="text.secondary">
              Duration: {data.durationMs != null ? `${data.durationMs} ms` : '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Started: {data.startedAt ? new Date(data.startedAt).toLocaleString() : '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed: {data.completedAt ? new Date(data.completedAt).toLocaleString() : '—'}
            </Typography>
          </Box>

          {/* Breadcrumb: Function Name, Slug */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600}>
              {data.agentFunctionName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data.agentFunctionSlug}
            </Typography>
          </Box>

          <Divider />

          {/* Error message if failed */}
          {data.status === 3 && data.errorMessage && (
            <Alert severity="error">{data.errorMessage}</Alert>
          )}

          {/* Input JSON */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Input
            </Typography>
            <CodeEditor
              value={data.inputJson || '{}'}
              language="json"
              height="200px"
              readOnly
            />
          </Box>

          {/* Output JSON */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Output
            </Typography>
            {data.outputJson ? (
              <CodeEditor
                value={formatOutputJson(data.outputJson)}
                language="json"
                height="200px"
                readOnly
              />
            ) : (
              <>
                <CodeEditor value="{}" language="json" height="100px" readOnly />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No output yet
                </Typography>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Tab 1: Session Data */}
      {tabIndex === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {session ? (
            <>
              {/* Session summary */}
              <Card
                sx={{
                  borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
                  border: '1px solid var(--glass-border)',
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {session.sessionName || 'Session'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    <Chip
                      label={
                        ['Active', 'Paused', 'Completed', 'Cancelled', 'Expired', 'Error'][
                          session.status
                        ] ?? 'Unknown'
                      }
                      size="small"
                      color={
                        session.status === 2
                          ? 'success'
                          : session.status === 5
                            ? 'error'
                            : 'default'
                      }
                    />
                    <Typography variant="body2" color="text.secondary">
                      Messages: {session.messageCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tokens: {session.totalTokensUsed ?? 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cost: ${(session.totalCost ?? 0).toFixed(4)}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenIcon />}
                    onClick={() =>
                      navigate(ROUTES.SESSION_DETAIL.replace(':id', session.id))
                    }
                    sx={{ mt: 2 }}
                  >
                    Open full session
                  </Button>
                </CardContent>
              </Card>

              {/* Message list */}
              <Typography variant="subtitle1" fontWeight={600}>
                Messages
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {messages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No messages
                  </Typography>
                ) : (
                  messages.map((msg) => (
                    <Card
                      key={msg.id}
                      sx={{
                        borderRadius: 'var(--mui-shape-customBorderRadius-md)',
                        border: '1px solid var(--glass-border)',
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                          }}
                        >
                          <Chip
                            label={getRoleLabel(msg.role)}
                            size="small"
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {msg.createdAt
                              ? new Date(msg.createdAt).toLocaleString()
                              : ''}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            fontSize: '0.8125rem',
                          }}
                        >
                          {msg.content || '(empty)'}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No session data available for this request.
            </Typography>
          )}
        </Box>
      )}

      {/* Tab 2: Files */}
      {tabIndex === 2 && (
        <Box>
          {files.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No files attached
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
                overflow: 'hidden',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Content Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Size
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell>{f.originalFileName || f.fileName}</TableCell>
                      <TableCell>{f.contentType}</TableCell>
                      <TableCell align="right">{f.fileSizeBytes}</TableCell>
                      <TableCell>
                        {f.createdAt
                          ? new Date(f.createdAt).toLocaleString()
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};
